/** Learner space of THE BRIDGE WORLD Academy (public routes /academy/*): sign-up, placement, exams, results. */

import "./academy.css";
import "./sales/sales.css";

import { Fragment, type ReactNode, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";

import { session, useSession } from "../lib/session";
import { AcademyHome } from "./AcademyHome";
import { AcademyLayout } from "./AcademyLayout";
import { AcademyLanding } from "./AcademyLanding";
import { ChallengePage } from "./ChallengePage";
import { OralPage } from "./coaching/OralPage";
import { PlayerSwitch, ResultsSwitch } from "./ielts/AttemptSwitch";
import { FreeMockNext } from "./sales/FreeMockCard";
import { CoursePage } from "./ielts/CoursePage";
import { ByExam, ExamProvider, useActiveExam } from "./ielts/exam";
import { IeltsChallengePage } from "./ielts/IeltsChallenge";
import { IeltsHome } from "./ielts/IeltsHome";
import { IeltsProgressPage } from "./ielts/IeltsProgress";
import "./ielts/ielts.css";
import "./ielts/course.css";
import "./history.css";
import { LessonPage } from "./ielts/LessonPage";
import { MethodPage } from "./ielts/MethodPage";
import { ReviewPage } from "./ielts/ReviewPage";
import { AttemptHistoryPanel, HistoryChallengePage, HistoryLessonPage } from "./HistoryCorrections";
import { HistoryPage } from "./HistoryPage";
import { LivePage } from "./LivePage";
import { MocksPage } from "./MocksPage";
import { ProgressPage } from "./ProgressPage";
import { AccountPage } from "./sales/AccountPage";
import { rememberCampaign } from "./sales/campaign";
import { CheckoutPage } from "./sales/CheckoutPage";
import { ChoosePlanPage } from "./sales/ChoosePlanPage";
import { LegalPage } from "./sales/LegalPage";
import { GoetheHome } from "./goethe/GoetheHome";
import { GoetheProgressPage } from "./goethe/GoetheProgress";
import { TefHome } from "./tef/TefHome";
import { TefProgressPage } from "./tef/TefProgress";
import { HelpPage, HelpTicketPage } from "./SupportPages";
import { TutorPage } from "./TutorPage";

/** Restores the cookie session once; `signedIn` then tells where to go. */
function useLearnerSession(): { ready: boolean; signedIn: boolean } {
  const { accessToken, ready } = useSession();
  useEffect(() => {
    if (!ready && !accessToken) void session.restore();
  }, [ready, accessToken]);
  return { ready: ready || Boolean(accessToken), signedIn: Boolean(accessToken) };
}

function RequireLearner({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const { ready, signedIn } = useLearnerSession();
  if (signedIn) return children;
  if (!ready) return <p className="app-splash ac-splash">{t("common.loading")}</p>;
  return <Navigate to="/academy" replace />;
}

function Landing() {
  const { t } = useTranslation();
  const { ready, signedIn } = useLearnerSession();
  if (!ready) return <p className="app-splash ac-splash">{t("common.loading")}</p>;
  if (signedIn) return <Navigate to="/academy/home" replace />;
  return <AcademyLanding />;
}

/** Screens whose local state belongs to one exam start afresh when the learner switches exam. */
function PerExam({ children }: { children: ReactNode }) {
  const { exam } = useActiveExam();
  return <Fragment key={exam}>{children}</Fragment>;
}

/** D-172: an old link of the site's demo leads to the sign-up, its query (exam, UTM) kept. */
function LegacyDemoRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/academy${search}`} replace />;
}

/** The results of a sitting; under those of the free mock, the plans (D-172). */
function ResultsWithNext() {
  const { id = "" } = useParams();
  return (
    <>
      <ResultsSwitch />
      <AttemptHistoryPanel attempt={id} />
      <FreeMockNext attempt={id} />
    </>
  );
}

export function AcademyApp() {
  return (
    <ExamProvider>
      <AcademyRoutes />
    </ExamProvider>
  );
}

function AcademyRoutes() {
  const location = useLocation();
  useEffect(() => rememberCampaign(location.search), [location.search]); // UTM, referral (D-110)
  return (
    <AcademyLayout>
      <Routes>
        <Route index element={<Landing />} />
        <Route
          path="home"
          element={
            <RequireLearner>
              <ByExam tcf={<AcademyHome />} ielts={<IeltsHome />} tef={<TefHome />} goethe={<GoetheHome />} />
            </RequireLearner>
          }
        />
        <Route
          path="progress"
          element={
            <RequireLearner>
              <ByExam tcf={<ProgressPage />} ielts={<IeltsProgressPage />} tef={<TefProgressPage />} goethe={<GoetheProgressPage />} />
            </RequireLearner>
          }
        />
        <Route
          path="history"
          element={
            <RequireLearner>
              <PerExam>
                <HistoryPage />
              </PerExam>
            </RequireLearner>
          }
        />
        <Route
          path="history/lesson/:id"
          element={
            <RequireLearner>
              <HistoryLessonPage />
            </RequireLearner>
          }
        />
        <Route
          path="history/challenge/:id"
          element={
            <RequireLearner>
              <HistoryChallengePage />
            </RequireLearner>
          }
        />
        <Route
          path="mocks"
          element={
            <RequireLearner>
              <PerExam>
                <ByExam tcf={<MocksPage />} ielts={<MocksPage />} tef={<MocksPage />} goethe={<GoetheHome />} />
              </PerExam>
            </RequireLearner>
          }
        />
        <Route
          path="challenge"
          element={
            <RequireLearner>
              <ByExam
                tcf={<ChallengePage />}
                ielts={<IeltsChallengePage />}
                tef={<IeltsChallengePage exam="tef_canada" />}
                goethe={<IeltsChallengePage exam="goethe" />}
              />
            </RequireLearner>
          }
        />
        <Route
          path="live"
          element={
            <RequireLearner>
              <PerExam>
                <LivePage />
              </PerExam>
            </RequireLearner>
          }
        />
        <Route
          path="tutor"
          element={
            <RequireLearner>
              <PerExam>
                <TutorPage />
              </PerExam>
            </RequireLearner>
          }
        />
        <Route
          path="plans"
          element={
            <RequireLearner>
              <ChoosePlanPage />
            </RequireLearner>
          }
        />
        <Route
          path="checkout/:id"
          element={
            <RequireLearner>
              <CheckoutPage />
            </RequireLearner>
          }
        />
        <Route
          path="oral"
          element={
            <RequireLearner>
              <OralPage />
            </RequireLearner>
          }
        />
        <Route
          path="account"
          element={
            <RequireLearner>
              <AccountPage />
            </RequireLearner>
          }
        />
        <Route
          path="support"
          element={
            <RequireLearner>
              <HelpPage />
            </RequireLearner>
          }
        />
        <Route
          path="support/:id"
          element={
            <RequireLearner>
              <HelpTicketPage />
            </RequireLearner>
          }
        />
        <Route path="legal/:page" element={<LegalPage />} />
        {/* D-172: the site's demo is gone, its old links reach the sign-up (exam and UTM kept) */}
        <Route path="demo/*" element={<LegacyDemoRedirect />} />
        <Route
          path="attempt/:id"
          element={
            <RequireLearner>
              <PlayerSwitch />
            </RequireLearner>
          }
        />
        <Route
          path="attempt/:id/results"
          element={
            <RequireLearner>
              <ResultsWithNext />
            </RequireLearner>
          }
        />
        <Route
          path="course"
          element={
            <RequireLearner>
              <CoursePage />
            </RequireLearner>
          }
        />
        <Route
          path="course/lesson/:id"
          element={
            <RequireLearner>
              <LessonPage />
            </RequireLearner>
          }
        />
        <Route
          path="course/method"
          element={
            <RequireLearner>
              <MethodPage />
            </RequireLearner>
          }
        />
        <Route
          path="course/review"
          element={
            <RequireLearner>
              <ReviewPage />
            </RequireLearner>
          }
        />
        <Route path="*" element={<Navigate to="/academy" replace />} />
      </Routes>
    </AcademyLayout>
  );
}
